// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC

<template>
  <div class="item-list">
    <v-card>
      <v-card-title v-if="search && !pagination">
        <v-text-field
          append-icon="search"
          label="Search"
          single-line
          hide-details
          v-model="searchText"
        ></v-text-field>
      </v-card-title>
      <v-card-title v-else-if="search && pagination">
        <v-text-field
          v-model="searchTextApi"
          append-icon="clear"
          :append-icon-cb="searchClear"
          prepend-icon="search"
          :prepend-icon-cb="searchApi"
          label="Search"
          hide-details
          v-on:keyup.enter="searchApi()"
        ></v-text-field>
      </v-card-title>
      <v-data-table
      mobile-breakpoint="0"
      v-bind="computedProps"
      :items="items"
      :loading="loading"
      :search="searchText"
      :options.sync="paginationVal"
      :no-data-text="noDataText"
      :multi-sort="false"
      :must-sort="false"
      :items-per-page="25"
      :footer-props="{
        'items-per-page-options': [10, 25, 100]
      }"
      >
        <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
        <template slot="item" slot-scope="props">
          <tr :style="rowStyle" :class="rowClass(props.item)" @click="editItem(props.item[id])">
            <template v-for="(header, i) in headers">
              <td v-if="header.function || header.itemValue" class="text-xs-left" :key="i">
                {{ computedFromContext[header.value][getElement(props.item, id)] }}
              </td>
              <td v-else-if="header.type=='checkbox'" :key="i">
                <v-checkbox
                :input-value="getElement(props.item, header.value)"
                disabled
                hide-details
                style="width: 1px; margin-top:0px;">
                </v-checkbox>
              </td>
              <td v-else class="text-xs-left" :key="i">
                {{ getElement(props.item, header.value) }}
              </td>
            </template>
            <template v-for="(button, j) in buttons">
              <td style="width: 1%; padding: 0px;" :key="j + 10000">
                <v-btn :title="button.text" icon text @click="button.function(props.item)"><v-icon>{{ button.icon }}</v-icon></v-btn>
              </td>
            </template>

            <td style="width: 1%; padding: 0px;" v-if="allowDelete && canDelete">
              <v-btn title="Delete" icon text @click.stop="deleteDialog(props.item)"><v-icon>delete</v-icon></v-btn>
            </td>
          </tr>
        </template>
        <template slot="footer.prepend">
          <slot name="footer.prepend"></slot>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="showDeleteDialog" max-width="300px">
      <v-card>
        <v-card-title>
          <div class="headline">Delete?</div>
        </v-card-title>
        <v-card-text>Are you sure you want to delete this {{ name }}?  This action cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click.native="showDeleteDialog = false">Cancel</v-btn>
          <v-btn text color="red" @click.native="deleteItem">I'm Sure</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-btn v-if="extendedFab && allowAdd && canCreate" dark class="green mt-4 py-6 px-6 rounded-lg" @click="editItem('new')" id="addButton">{{ extendedFabText }}<v-icon right>add</v-icon></v-btn>
    <v-btn v-else-if="!extendedFab && allowAdd && canCreate" fab dark class="green mt-4" @click="editItem('new')" id="addButton"><v-icon>add</v-icon></v-btn>
    <error-snackbar v-model="errorText"></error-snackbar>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import ErrorSnackbar from '@sdgsystems/vue-common/components/ErrorSnackbar'

  import {AXIOSAPI} from './requests-common'
  import {checkPermission} from './user'
  import {PERMISSIONS} from './constants'

  export default {
    name: 'item-list',

    components: {
      ErrorSnackbar
    },

    data () {
      return {
        contextData: [],
        deleteQueue: null,
        errorText: '',
        items: [],
        loading: true,
        oldQuery: null,
        paginationVal: {
          page: 1,
          itemsPerPage: 25,
          sortBy: [''],
          sortDesc: [false],
        },
        searchText: null,
        searchTextApi: '',
        showDeleteDialog: false,
        totalItems: 0,
        // debounce time in milliseconds
        debounceTime: 300,
        // debounce variable to hold timeout
        debounceTimeout: null,
      }
    },

    props: {
      allowAdd: Boolean,
      allowDelete: Boolean,
      buttons: Array,
      // path to navigate to (with value at the id prop as a param) or
      // a function that will be passed the value at the id prop.
      editRoute: null,
      endpoint: String,
      // Filters for requested data so data can be queried using data from parent object.
      filter: Array,
      headers: Array,
      id: {
        type: String,
        default: 'id'
      },
      loadData: {
        type: Boolean,
        default: true
      },
      // Use v-model instead of fetching data.
      model: Boolean,
      name: String,
      // Text displayed when there is no data.
      noDataText: String,
      // If true, use API pagination and searching.
      pagination: Boolean,
      // Check if the user has permission to edit this resource.
      permissions: {
        type: String,
        default: ''
      },
      rowClass: {
        type: Function,
        default: () => {}
      },
      // If true, display an internal search bar and use API searching.
      search: Boolean,
      // If true, use fuzzy search. Implies search is true.
      fuzzySearch: Boolean,
      searchBarText: String,
      value: Array,
      extendedFab: {
        type: Boolean,
        default: false
      },
      extendedFabText: String
    },

    created () {
      this.updatePagination()
      this.searchText = this.searchBarText
      Promise.all(this.loadContext()).then(() => {
        if (this.$props.loadData !== false && !this.$props.model) {
          this.getData()
        } else {
          this.loading = false
        }
      })
    },

    watch: {
      paginationVal: function () {
        if (this.$props.pagination && !this.$props.model && this.$props.loadData) {
          this.updatePath()
          this.searchDebounce()
        }
      },

      loadData: function () {
        if (this.$props.loadData === true && !this.$props.model) {
          this.getData(true)
        }
      },

      filter: function () {
        if (this.$props.loadData === true && !this.$props.model) {
          this.getData()
        }
      },

      '$route.query': function () {
        this.updatePagination()
        this.getData()
      },

      value: function () {
        if (this.$props.model) {
          this.items = this.value
        }
      },

      searchBarText: function (newVal, oldVal) {
        if (!this.$props.pagination) {
          this.searchText = newVal
        }
        else {
          this.paginationVal.page = 1
        }
        this.searchDebounce()
      }
    },

    computed: {
      ...mapGetters([
        'accessRules'
      ]),

      canCreate: function () {
        return checkPermission(this.accessRules, this.$props.permissions, PERMISSIONS.CREATE)
      },

      canDelete: function () {
        return checkPermission(this.accessRules, this.$props.permissions, PERMISSIONS.DELETE)
      },

      computedHeaders: function () {
        var headers = []
        for (var i in this.$props.headers) {
          var header = {
            text: this.$props.headers[i].text,
            value: this.$props.headers[i].value,
            align: 'left',
            sortable: this.$props.headers[i].sortable,
          }
          headers.push(header)
        }
        if ((this.allowDelete && this.canDelete) || this.buttons) {
          headers.push({
            text: 'Actions',
            value: 'name',
            sortable: false
          })
        }

        return headers
      },

      computedProps: function () {
        var props = {}
        props['headers'] = this.computedHeaders
        if (this.serverPagination) {
          props['server-items-length'] = this.totalItems
        }

        return props
      },

      computedFromContext: function () {
        var lists = []
        for (var i in this.$props.headers) {
          var header = this.$props.headers[i]
          if (header.function || header.context) {
            var list = []
            for (var j in this.items) {
              var item = this.items[j]
              var value = 'n/a'
              if (header.function) {
                value = header.function(item, this.contextData[header.context])
              } else {
                if (this.contextData[header.context]) {
                  var foundObject = this.contextData[header.context].find((el) => el[header.itemValue] === this.getElement(item, header.value))
                  if (foundObject) {
                    value = this.getElement(foundObject, header.itemText)
                  }
                }
              }
              list[this.getElement(item, this.$props.id)] = value
            }
            lists[header.value] = list
          }
        }
        return lists
      },

      context: function () {
        var context = []
        for (var i in this.$props.headers) {
          var contextPath = this.$props.headers[i].context
          if (contextPath) {
            if (!context.find(el => el === contextPath)) {
              context.push(contextPath)
            }
          }
        }
        return context
      },

      rowStyle: function () {
        if (this.$props.editRoute) {
          return 'cursor:pointer;'
        }
        return ''
      },

      serverPagination: function () {
        return this.$props.pagination;
      },

      sortBy: function () {
        var sort = ''
        if (this.paginationVal.sortBy && this.paginationVal.sortBy.length > 0 && this.paginationVal.sortBy[0] !== '') {
          if (!this.paginationVal.sortDesc[0]) {
            sort = '-'
          }
          sort = sort + this.paginationVal.sortBy[0]
        }
        return sort
      }
    },

    methods: {
      deleteDialog: function (row) {
        this.showDeleteDialog = true
        this.deleteQueue = row
      },

      deleteItem: function () {
        this.showDeleteDialog = false
        AXIOSAPI.delete(this.$props.endpoint + '/' + this.deleteQueue[this.$props.id]).then(response => {
          this.showDeleteDialog = false
          this.getData(true)
        }).catch(e => {
          this.errorText = 'Delete failed: ' + e.response.data
          console.log(e.response)
        })
        this.deleteQueue = null
      },

      editElement: function (value, row, item) {
        row[item] = value
        this.editInline(row)
      },

      editInline: function (line) {
        AXIOSAPI.put(this.$props.endpoint + '/' + line[this.$props.id], line).then().catch(e => {
          this.errorText = 'Save failed: ' + e.response.data
          console.log(e.response)
        })
      },

      editItem: function (id) {
        if (this.$props.editRoute) {
          if (id === '') {
            id = 'new'
          }
          if (typeof this.$props.editRoute === 'function') {
            this.$props.editRoute(id)
          } else {
            this.$router.push({name: this.$props.editRoute, params: {id: id}})
          }
        }
      },

      searchDebounce: function () {
        // 3 character minimum search, allow search to be cleared
        if (this.searchBarText.length < 3 && this.searchBarText.length > 0) {
          return
        }

        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout)
        }

        this.debounceTimeout = setTimeout(() => {
          this.getData()
        }, this.debounceTime)
      },

      getData: function (refresh) {
        var query = {}
        if (this.$props.filter) {
          for (var i in this.$props.filter) {
            var filter = this.$props.filter[i]
            if (typeof filter.value === 'object') {
              var val = ''
              for (var j in filter.value) {
                val = val + filter.value[j] + ','
              }
              query[filter.name] = val.slice(0, -1)
            } else {
              query[filter.name] = filter.value
            }
          }
        }

        if (this.$props.pagination) {
          query['limit'] = this.paginationVal.itemsPerPage
          query['page'] = (this.paginationVal.page - 1)

          if (this.sortBy !== '') {
            query['sort'] = this.sortBy
          }

          let search = ''

          if (this.$props.search) {
            search = this.searchTextApi
          }

          if (this.$props.searchBarText) {
            search = this.searchBarText
          }

          if (this.searchText) {
            search = this.searchText
          }

          if (this.$props.fuzzySearch) {
            query['search2'] = search
          } else {
            query['search'] = search
          }
        }

        var sameQuery = true
        for (var k in query) {
          if (this.oldQuery) {
            if (query[k] !== this.oldQuery[k]) {
              sameQuery = false
              break
            }
          }
        }

        // check old and new queries so items/added or removed are caught correctly
        for (var k in this.oldQuery) {
          if (query) {
            if (query[k] !== this.oldQuery[k]) {
              sameQuery = false
              break
            }
          }
        }

        // Don't make another request if the query hasn't changed
        if (sameQuery === true && this.oldQuery && !refresh) {
          return
        }

        this.oldQuery = query
        this.loading = true
        AXIOSAPI.get(this.$props.endpoint, { 'params': query }).then(response => {
          this.items = response.data
          if (this.$props.pagination) {
            this.totalItems = parseInt(response.headers['x-total-count'])
          }
          this.loading = false
        }).catch(e => {
          this.errorText = 'Failed to load ' + this.$props.name + ': ' + e
        })
      },

      getElement: function (item, element) {
        var path = element.split('.')
        var obj = item
        while (path.length) {
          obj = obj[path.shift()]
        }
        return obj
      },

      loadContext: function () {
        // return a list of promises so we can wait until this function returns to do other stuff
        var dostuff = function (context, location, response) {
          context.$set(context.contextData, location, response.data)
        }

        var list = []
        this.context.forEach((arrayElement) => {
          var prom = AXIOSAPI.get(arrayElement).then(response => {
            dostuff(this, arrayElement, response)
          })
            .catch(e => {
              this.errorText = 'Failed to load ' + arrayElement + ': ' + e
            })
          list.push(prom)
        })
        return list
      },

      searchApi: function () {
        if (this.searchTextApi !== this.$route.query.search) {
          this.getData()
          var query = Object.assign({}, this.$route.query)
          query.search = this.searchTextApi
          this.$router.push({name: this.$route.name, query: query})
        }
      },

      searchClear: function () {
        this.searchTextApi = ''
        this.searchApi()
      },

      updatePagination: function () {
        if (this.$route.query.page) {
          this.paginationVal.page = parseInt(this.$route.query.page) + 1
        }
        if (this.$route.query.limit) {
          this.paginationVal.rowsPerPage = this.$route.query.limit
        }
        if (this.$route.query.sort) {
          this.paginationVal.descending = (this.$route.query.sort.indexOf('-') < 0)
          this.paginationVal.sortBy = [this.$route.query.sort.replace('-', '')]
        }
        if (this.$route.query.search) {
          this.searchTextApi = this.$route.query.search
        }
      },

      updatePath: function () {
        var query = Object.assign({}, this.$route.query)
        query.page = this.paginationVal.page - 1
        query.limit = parseInt(this.paginationVal.itemsPerPage)
        query.sort = this.sortBy

        if (query.page !== 0 || query.limit !== 25 || query.sort !== '') {
          this.$router.push({name: this.$route.name, query: query})
        }
      }
    }
  }
</script>

<style scoped>
  #addButton {
    position: relative;
    overflow: hidden;
    position: fixed;
    bottom: 20px !important;
    right: 20px;
  }
</style>



// WEBPACK FOOTER //
// ItemList.vue?5a3ffad6